import { useCallback, useEffect, useState } from 'react';

/*
  Moneda de visualización de los planes (sitio completo).

  Los precios NO se convierten por API: cada plan declara un valor comercial
  fijo por moneda. La tasa de referencia usada para fijarlos es 1 USD ≈ 59 DOP,
  redondeando hacia abajo a la centena de 50 más cercana.

  La preferencia se comparte entre los planes generales, los de dealers y la
  página de Odontología, y se guarda en localStorage.
*/

export type Currency = 'USD' | 'DOP';

const STORAGE_KEY = 'innoflex-currency';

/** Prefijo visible de cada moneda. */
export const CURRENCY_LABEL: Record<Currency, string> = {
  USD: 'US$',
  DOP: 'RD$',
};

/** Sufijo corto usado junto al precio ("USD/mes", "DOP/mes"). */
export const CURRENCY_CODE: Record<Currency, string> = {
  USD: 'USD',
  DOP: 'DOP',
};

/* ── Términos de facturación ───────────────────────────────────────────────
   El descuento es PERMANENTE por término, no una promoción de primer periodo:
   el precio que se ve al contratar es el que se paga en cada renovación.

   · Semestral → 10% de descuento (se pagan 5.4 mensualidades de 6)
   · Anual     → 2 meses gratis  (se pagan 10 mensualidades de 12 ≈ 16.7%)

   Al pagar anual, la implementación va al 50%.
────────────────────────────────────────────────────────────────────────── */

export type BillingTerm = 'monthly' | 'semiannual' | 'annual';

export const BILLING_TERMS: BillingTerm[] = ['monthly', 'semiannual', 'annual'];

/** Meses de servicio que cubre cada término. */
export const TERM_MONTHS: Record<BillingTerm, number> = {
  monthly: 1,
  semiannual: 6,
  annual: 12,
};

/** Mensualidades que se cobran en cada término. */
const TERM_BILLED_MONTHS: Record<BillingTerm, number> = {
  monthly: 1,
  semiannual: 5.4,
  annual: 10,
};

/** Parte de la implementación que se perdona en cada término. */
export const TERM_IMPL_DISCOUNT: Record<BillingTerm, number> = {
  monthly: 0,
  semiannual: 0,
  annual: 0.5,
};

/** Total a pagar por adelantado en el término indicado. */
export function termTotal(monthly: number, term: BillingTerm): number {
  return monthly * TERM_BILLED_MONTHS[term];
}

/** Mensualidad equivalente del término (para comparar contra el precio mensual). */
export function termMonthlyEquivalent(monthly: number, term: BillingTerm): number {
  return termTotal(monthly, term) / TERM_MONTHS[term];
}

/** Cuánto se ahorra frente a pagar mes a mes durante el mismo periodo. */
export function termSavings(monthly: number, term: BillingTerm): number {
  return monthly * TERM_MONTHS[term] - termTotal(monthly, term);
}

/** Implementación ya con el descuento del término aplicado. */
export function termImplementation(implementation: number, term: BillingTerm): number {
  return implementation * (1 - TERM_IMPL_DISCOUNT[term]);
}

/**
 * Formatea un monto con el prefijo de la moneda: US$29.99 · US$150 · RD$1,750.
 * Los montos enteros se muestran sin decimales (una implementación de US$150
 * no se lee "US$150.00"); los que sí tienen céntimos conservan los dos.
 */
export function formatPrice(value: number, currency: Currency): string {
  const digits = currency === 'USD' && !Number.isInteger(value) ? 2 : 0;
  return (
    CURRENCY_LABEL[currency] +
    value.toLocaleString('en-US', {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    })
  );
}

function readStored(): Currency {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved === 'DOP' || saved === 'USD' ? saved : 'USD';
  } catch {
    return 'USD';
  }
}

/**
 * Moneda seleccionada para ver los precios. Se sincroniza entre las secciones
 * montadas a la vez (planes generales y dealers viven en la misma página).
 */
export function useCurrency(): {
  currency: Currency;
  setCurrency: (next: Currency) => void;
} {
  const [currency, setCurrencyState] = useState<Currency>(readStored);

  useEffect(() => {
    const sync = (e: Event) => {
      const next = (e as CustomEvent<Currency>).detail;
      if (next === 'USD' || next === 'DOP') setCurrencyState(next);
    };
    window.addEventListener('innoflex-currency-change', sync);
    return () => window.removeEventListener('innoflex-currency-change', sync);
  }, []);

  const setCurrency = useCallback((next: Currency) => {
    setCurrencyState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* almacenamiento no disponible — se mantiene solo en memoria */
    }
    window.dispatchEvent(new CustomEvent('innoflex-currency-change', { detail: next }));
  }, []);

  return { currency, setCurrency };
}
